import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, RotateCcw, X } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';

const CROP_SIZE = 256;
const PREVIEW_SIZE = 96;
const OUTPUT_SIZE = 512;

interface AvatarCropperProps {
  file: File;
  onConfirm: (file: File) => void;
  onCancel: () => void;
  onCropChange?: () => void;
}

interface Point {
  x: number;
  y: number;
}

interface NaturalSize {
  width: number;
  height: number;
}

interface DragState {
  pointerId: number;
  startX: number;
  startY: number;
  origin: Point;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function croppedFileName(file: File): string {
  const base = file.name.replace(/\.[^/.]+$/, '').trim() || 'avatar';
  return `${base}-crop.jpg`;
}

export const AvatarCropper: React.FC<AvatarCropperProps> = ({
  file,
  onConfirm,
  onCancel,
  onCropChange,
}) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<DragState | null>(null);

  const [sourceUrl, setSourceUrl] = useState('');
  const [naturalSize, setNaturalSize] = useState<NaturalSize | null>(null);
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const nextUrl = URL.createObjectURL(file);
    setSourceUrl(nextUrl);
    setNaturalSize(null);
    setOffset({ x: 0, y: 0 });
    setZoom(1);
    setError(null);
    return () => URL.revokeObjectURL(nextUrl);
  }, [file]);

  useEffect(() => {
    onCropChange?.();
  }, [file, offset.x, offset.y, zoom, onCropChange]);

  const metrics = useMemo(() => {
    if (!naturalSize) return null;
    const baseScale = Math.max(
      CROP_SIZE / naturalSize.width,
      CROP_SIZE / naturalSize.height
    );
    const scale = baseScale * zoom;
    const width = naturalSize.width * scale;
    const height = naturalSize.height * scale;
    return {
      baseScale,
      scale,
      width,
      height,
      maxOffsetX: Math.max(0, (width - CROP_SIZE) / 2),
      maxOffsetY: Math.max(0, (height - CROP_SIZE) / 2),
    };
  }, [naturalSize, zoom]);

  const clampOffset = useCallback((point: Point, nextZoom = zoom): Point => {
    if (!naturalSize) return point;
    const baseScale = Math.max(
      CROP_SIZE / naturalSize.width,
      CROP_SIZE / naturalSize.height
    );
    const width = naturalSize.width * baseScale * nextZoom;
    const height = naturalSize.height * baseScale * nextZoom;
    const maxOffsetX = Math.max(0, (width - CROP_SIZE) / 2);
    const maxOffsetY = Math.max(0, (height - CROP_SIZE) / 2);
    return {
      x: clamp(point.x, -maxOffsetX, maxOffsetX),
      y: clamp(point.y, -maxOffsetY, maxOffsetY),
    };
  }, [naturalSize, zoom]);

  const handleImageLoad = () => {
    const image = imageRef.current;
    if (!image) return;
    setNaturalSize({
      width: image.naturalWidth,
      height: image.naturalHeight,
    });
    setOffset({ x: 0, y: 0 });
    setZoom(1);
    setError(null);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!metrics) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origin: offset,
    };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const nextOffset = {
      x: drag.origin.x + event.clientX - drag.startX,
      y: drag.origin.y + event.clientY - drag.startY,
    };
    setOffset(clampOffset(nextOffset));
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    }
  };

  const handleZoomChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextZoom = Number(event.target.value);
    setZoom(nextZoom);
    setOffset((current) => clampOffset(current, nextZoom));
  };

  const handleReset = () => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  const createCroppedFile = async (): Promise<File> => {
    const image = imageRef.current;
    if (!image || !naturalSize || !metrics) {
      throw new Error('Avatar image is not ready.');
    }

    const imageLeft = CROP_SIZE / 2 - metrics.width / 2 + offset.x;
    const imageTop = CROP_SIZE / 2 - metrics.height / 2 + offset.y;
    const sourceX = (0 - imageLeft) / metrics.scale;
    const sourceY = (0 - imageTop) / metrics.scale;
    const sourceSize = CROP_SIZE / metrics.scale;

    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Unable to prepare avatar crop.');
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    context.drawImage(
      image,
      sourceX,
      sourceY,
      sourceSize,
      sourceSize,
      0,
      0,
      OUTPUT_SIZE,
      OUTPUT_SIZE
    );

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((result) => {
        if (result) resolve(result);
        else reject(new Error('Unable to create avatar image.'));
      }, 'image/jpeg', 0.92);
    });

    return new File([blob], croppedFileName(file), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  };

  const handleConfirm = async () => {
    setError(null);
    try {
      const cropped = await createCroppedFile();
      onConfirm(cropped);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to crop avatar.');
    }
  };

  const cropImageStyle = metrics
    ? {
        width: metrics.width,
        height: metrics.height,
        transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
      }
    : undefined;
  const previewRatio = PREVIEW_SIZE / CROP_SIZE;
  const previewImageStyle = metrics
    ? {
        width: metrics.width * previewRatio,
        height: metrics.height * previewRatio,
        transform: `translate(calc(-50% + ${offset.x * previewRatio}px), calc(-50% + ${offset.y * previewRatio}px))`,
      }
    : undefined;

  return (
    <div className="space-y-4 overflow-x-auto border-2 border-foreground bg-background p-3 shadow-neo-sm">
      <div className="grid gap-4 sm:grid-cols-[auto_1fr]">
        <div className="space-y-2">
          <Label>Crop</Label>
          <div
            className="relative h-64 w-64 max-w-full touch-none overflow-hidden border-2 border-foreground bg-muted"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
          >
            {sourceUrl && (
              <img
                ref={imageRef}
                src={sourceUrl}
                alt=""
                draggable={false}
                onLoad={handleImageLoad}
                onError={() => setError('Unable to preview this image.')}
                className="absolute left-1/2 top-1/2 max-w-none select-none"
                style={cropImageStyle}
              />
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Preview</Label>
            <div className="relative h-24 w-24 overflow-hidden rounded-full border-2 border-foreground bg-card shadow-neo-sm">
              {sourceUrl && (
                <img
                  src={sourceUrl}
                  alt=""
                  draggable={false}
                  className="absolute left-1/2 top-1/2 max-w-none select-none"
                  style={previewImageStyle}
                />
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="avatar-zoom">Zoom</Label>
            <input
              id="avatar-zoom"
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={handleZoomChange}
              className="w-full"
            />
          </div>

          {error && (
            <p className="border-2 border-destructive bg-destructive px-3 py-2 text-sm font-bold text-destructive-foreground">
              {error}
            </p>
          )}

          <div className="grid grid-cols-1 gap-2 min-[380px]:grid-cols-3 sm:flex sm:flex-wrap">
            <Button type="button" size="sm" className="w-full sm:w-auto" onClick={handleConfirm} disabled={!metrics}>
              <Check className="h-4 w-4" />
              Confirm crop
            </Button>
            <Button type="button" size="sm" variant="outline" className="w-full sm:w-auto" onClick={handleReset}>
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
            <Button type="button" size="sm" variant="outline" className="w-full sm:w-auto" onClick={onCancel}>
              <X className="h-4 w-4" />
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
